import { clsx, type ClassValue } from "clsx"
import { format, isBefore, addDays, startOfDay, parseISO } from "date-fns"
import { twMerge } from "tailwind-merge"

// =============================================================================
// TIMEZONE HELPERS
// =============================================================================

/**
 * WHY: Empty strings and invalid IANA values cause Intl.DateTimeFormat to throw
 * RangeError. Org records may have null/empty timezone from creation before the
 * field was added. Falls back to America/New_York (the default org timezone).
 */
export function normalizeOrgTimezone(tz: string | null | undefined): string {
  if (!tz || tz.trim() === "") return "America/New_York";
  try { Intl.DateTimeFormat(undefined, { timeZone: tz }); return tz; }
  catch { return "America/New_York"; }
}

/**
 * WHY: DST-safe midnight resolution. On DST transition days the offset at
 * midnight differs from the offset at noon. This function probes at noon UTC to
 * get a reliable offset, computes a midnight candidate, then verifies/adjusts by
 * re-probing at the candidate time. Converges in ≤2 Intl calls per midnight.
 * Exported for scripts that need the org-local midnight of a PAST day (orgDayBoundsUTC
 * only answers for today).
 */
export function resolveLocalMidnight(year: number, month: number, day: number, timezone: string): Date {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const getParts = (d: Date) => {
    const parts = fmt.formatToParts(d);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
    return {
      localMs: Date.UTC(
        get("year"), get("month") - 1, get("day"),
        get("hour") === 24 ? 0 : get("hour"), get("minute"), get("second"),
      ),
      utcMs: d.getTime(),
    };
  };

  // Probe at noon UTC on the target date to get a reliable offset sample
  const noonUTC = Date.UTC(year, month, day, 12, 0, 0);
  const noon = getParts(new Date(noonUTC));
  const offsetMs = noon.localMs - noon.utcMs;

  // Midnight candidate: local midnight expressed in UTC
  const midnightCandidate = Date.UTC(year, month, day) - offsetMs;

  // Verify: re-probe at the candidate time (DST may differ at midnight vs noon)
  const verify = getParts(new Date(midnightCandidate));
  const verifyOffsetMs = verify.localMs - midnightCandidate;

  if (verifyOffsetMs !== offsetMs) {
    return new Date(Date.UTC(year, month, day) - verifyOffsetMs);
  }
  return new Date(midnightCandidate);
}

/**
 * Convert a `YYYY-MM-DD` calendar day into the UTC instant of that day's
 * midnight in the given IANA timezone.
 *
 * WHY THIS EXISTS: date-only concepts stored in `DateTime` columns (rather than
 * `@db.Date`) silently acquire a time and a timezone. `new Date("2026-03-01")`
 * parses as UTC midnight, which renders as Feb 28 in every US timezone — an
 * off-by-one-day bug on every hand-entered date. Normalizing on WRITE means
 * every reader is correct by default and date differences compare like with like.
 *
 * WHY A WRAPPER AND NOT A NEW CONVERTER: resolveLocalMidnight already does this
 * job (DST-safe, ≤2 Intl calls) but is module-private because it only backed
 * orgDayBoundsUTC. This exposes it without duplicating the logic.
 *
 * Throws on a malformed input rather than silently producing an Invalid Date,
 * because a bad effective date must fail loudly at the validation boundary.
 */
export function orgLocalDateToUTC(dateString: string, timeZone: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString.trim());
  if (!match) {
    throw new Error(`orgLocalDateToUTC: expected YYYY-MM-DD, received "${dateString}"`);
  }
  // WHY DEFAULTS ON DESTRUCTURE: the regex guarantees all three groups matched,
  // but noUncheckedIndexedAccess types them as possibly-undefined. Defaulting
  // narrows to string without scattering non-null assertions; an empty string
  // would fail the round-trip check below rather than slipping through.
  const [, yearStr = "", monthStr = "", dayStr = ""] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  // WHY ROUND-TRIP VALIDATE RATHER THAN RANGE-CHECK: Date.UTC silently rolls
  // impossible dates over — "2026-02-31" becomes March 3 and "2026-13-01"
  // becomes Jan 2027, storing a plausible-looking but wrong effective date. A
  // month<=12/day<=31 bounds test does not catch Feb 31 or Apr 31; re-reading
  // the components back off the constructed date is the only check that does.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    throw new Error(`orgLocalDateToUTC: invalid calendar date "${dateString}"`);
  }

  return resolveLocalMidnight(year, month - 1, day, normalizeOrgTimezone(timeZone));
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * WHY: Converts a UTC Date to a "fake local" Date whose getFullYear/getMonth/
 * getDate/getHours/etc. match the real wall-clock values in the target timezone.
 * This lets date-fns format() — which always reads local components — produce
 * timezone-correct output. Works identically on server (UTC) and client (any TZ),
 * preventing React hydration mismatches.
 *
 * Uses Intl.DateTimeFormat.formatToParts for reliable timezone conversion
 * without external dependencies.
 */
export function toTzDate(date: Date | string, timeZone: string): Date {
  const d = date instanceof Date ? date : new Date(date);
  // WHY: Callers may pass raw org.timezone which can be "" or invalid.
  // Normalizing here prevents RangeError from Intl.DateTimeFormat and
  // protects all downstream callers (formatDate, formatDateTime, etc.).
  const tz = normalizeOrgTimezone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return new Date(
    get("year"),
    get("month") - 1,
    get("day"),
    // WHY: Intl may return hour 24 for midnight in some locales
    get("hour") === 24 ? 0 : get("hour"),
    get("minute"),
    get("second"),
  );
}

/**
 * Format a timestamp as "MM/dd/yyyy" in the specified timezone.
 * WHY TIMEZONE PARAM: Ensures server (UTC) and client (local TZ) produce
 * identical output, preventing React hydration mismatch (#418).
 * Defaults to UTC for backward compatibility with callers that don't pass a timezone.
 */
export function formatDate(date: Date | string | null | undefined, timeZone = "UTC"): string {
  if (!date) return "-";
  return format(toTzDate(date, timeZone), "MM/dd/yyyy");
}

/**
 * Format a timestamp as "MM/dd/yyyy h:mm a" in the specified timezone.
 * WHY TIMEZONE PARAM: Same hydration safety as formatDate.
 */
export function formatDateTime(date: Date | string | null | undefined, timeZone = "UTC"): string {
  if (!date) return "-";
  return format(toTzDate(date, timeZone), "MM/dd/yyyy h:mm a");
}

/**
 * Format a timestamp as "MMM d, yyyy 'at' h:mm a" in the specified timezone.
 * WHY: This is the most common format across 20+ components (audit trails,
 * notes, emails, status timelines). Centralizing it eliminates format string
 * drift and ensures consistent timezone handling.
 */
export function formatTimestamp(date: Date | string | null | undefined, timeZone = "UTC"): string {
  if (!date) return "-";
  return format(toTzDate(date, timeZone), "MMM d, yyyy 'at' h:mm a");
}

/**
 * WHY: DTO date-only fields (startDate, endDate, dateOfBirth) are serialized
 * as full ISO strings ("2015-03-15T00:00:00.000Z"). Passing that to new Date()
 * or parseISO gives midnight UTC, which shifts back 1 day in US timezones.
 * This helper strips the time/timezone portion before parsing so the calendar
 * date is preserved regardless of the user's timezone.
 */
export function formatDateOnly(
  date: Date | string | null | undefined,
  pattern = "MM/dd/yyyy",
): string {
  if (!date) return "-";
  try {
    const dateOnly = date instanceof Date
      ? date.toISOString().slice(0, 10)
      : date.slice(0, 10);
    return format(parseISO(dateOnly), pattern);
  } catch {
    return "-";
  }
}

/**
 * WHY: Computes "today" and "tomorrow" boundaries in UTC for a given IANA
 * timezone. Prisma stores all DateTimes as UTC, so queries like
 * `nextActionDate < tomorrow` need UTC boundaries that represent the org's
 * local day, not the server's UTC day.
 *
 * WHY INDEPENDENT RESOLUTION: DST transitions make some days 23h (spring
 * forward) or 25h (fall back). Resolving each midnight independently via
 * resolveLocalMidnight handles this correctly, unlike the old approach of
 * adding 86_400_000 to todayStart.
 */
export function orgDayBoundsUTC(timezone: string): { todayStart: Date; tomorrowStart: Date } {
  const tz = normalizeOrgTimezone(timezone);
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  const year = get("year");
  const month = get("month") - 1;
  const day = get("day");

  const todayStart = resolveLocalMidnight(year, month, day, tz);
  const tomorrowStart = resolveLocalMidnight(year, month, day + 1, tz);

  return { todayStart, tomorrowStart };
}

/**
 * Today's calendar date in the org's timezone, as UTC midnight — the storage
 * convention this codebase uses for date-only values held in `DateTime` columns.
 *
 * WHY NOT `new Date()`: Vercel runs in UTC, so a termination recorded at 7pm
 * Central on the 31st stores the 1st. It also stores a time of day, while every
 * sibling date-only column holds midnight (`providerStartDate` is written as
 * `new Date(input + "T00:00:00.000Z")`), so a bare `new Date()` disagrees with
 * `formatDateOnly` and the roster's `formatDateUTC`, both of which read UTC
 * components.
 *
 * WHY NOT `orgLocalDateToUTC`: that returns the org's local midnight expressed in
 * UTC, which is right for query boundaries and wrong for storage. For a timezone
 * ahead of UTC it lands on the previous UTC day, so reading the value back by UTC
 * components yields yesterday.
 */
export function orgTodayAsUtcDateOnly(timeZone: string): Date {
  const local = toTzDate(new Date(), timeZone);
  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
}

/**
 * Today in the org's timezone as a `YYYY-MM-DD` string — what a `<input type="date">` takes.
 *
 * WHY A SEPARATE HELPER RATHER THAN toISOString() ON THE ABOVE: they agree today, but the
 * pairing is what a dialog's default and its `max` both depend on, and reading it off a Date
 * invites someone to reach for the org-LOCAL variant later and land a day out.
 */
export function orgTodayIsoDate(timeZone: string): string {
  const local = toTzDate(new Date(), timeZone);
  const mm = String(local.getMonth() + 1).padStart(2, "0");
  const dd = String(local.getDate()).padStart(2, "0");
  return `${local.getFullYear()}-${mm}-${dd}`;
}

/**
 * WHY: Dashboard stats need period-start boundaries (today/week/month/quarter)
 * in UTC aligned to the org's local timezone. Uses resolveLocalMidnight for
 * DST-safe computation of each boundary.
 */
export function orgPeriodStartUTC(
  timezone: string,
  period: "today" | "week" | "month" | "quarter"
): Date {
  const tz = normalizeOrgTimezone(timezone);
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  const y = get("year");
  const m = get("month") - 1;
  const d = get("day");

  switch (period) {
    case "today":
      return resolveLocalMidnight(y, m, d, tz);
    case "week": {
      // WHY: JS Date getDay() is 0=Sun. We want Monday start (weekStartsOn: 1).
      const dow = new Date(y, m, d).getDay();
      const mondayOffset = dow === 0 ? 6 : dow - 1;
      return resolveLocalMidnight(y, m, d - mondayOffset, tz);
    }
    case "month":
      return resolveLocalMidnight(y, m, 1, tz);
    case "quarter": {
      const quarterMonth = m - (m % 3);
      return resolveLocalMidnight(y, quarterMonth, 1, tz);
    }
  }
}

/**
 * WHY: Computes the UTC instant of midnight N calendar days from the org's
 * "today". Uses resolveLocalMidnight so DST transitions are handled correctly
 * — a 90-day window doesn't silently drift by an hour on spring-forward/
 * fall-back weeks the way `today.getTime() + N * 86_400_000` would.
 */
export function orgDayOffsetUTC(timezone: string, dayOffset: number): Date {
  const tz = normalizeOrgTimezone(timezone);
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return resolveLocalMidnight(get("year"), get("month") - 1, get("day") + dayOffset, tz);
}

/**
 * Whole calendar days from the org's today to a date-only value (stored as UTC midnight):
 * 0 = today, negative = past. `now` is injectable for tests.
 *
 * WHY CALENDAR DIGITS, NOT INSTANT DIVISION: dividing (date - orgTodayStart) by 24h and rounding
 * is off by one for every US org, because a UTC-midnight date is the previous evening there.
 * Math.floor turned "due in 10 days" into 9 and "expires today" into "expired 1 day ago". Comparing
 * the stored date's UTC calendar day with the org's local calendar day is exact.
 */
export function calendarDaysUntil(dateOnly: Date, timezone: string, now: Date = new Date()): number {
  const tz = normalizeOrgTimezone(timezone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  const today = Date.UTC(get("year"), get("month") - 1, get("day"));
  const target = Date.UTC(dateOnly.getUTCFullYear(), dateOnly.getUTCMonth(), dateOnly.getUTCDate());
  return Math.round((target - today) / 86_400_000);
}

/**
 * The org's calendar date `dayOffset` days from today, as a date-only value (UTC midnight).
 * WHY: date-only columns (expiresOn, revalidationDate) are stored at UTC midnight, so a Prisma
 * bound on them must be a UTC-midnight date too. orgDayBoundsUTC returns the org's midnight as an
 * instant (05:00Z in New York), which puts a value dated today on the wrong side of the bound.
 */
export function orgCalendarDateUTC(timezone: string, dayOffset = 0, now: Date = new Date()): Date {
  const tz = normalizeOrgTimezone(timezone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return new Date(Date.UTC(get("year"), get("month") - 1, get("day") + dayOffset));
}

/**
 * Whole calendar days from an INSTANT (a timestamp, not a date-only value) to the org's today,
 * both read on the org's calendar: an event at 11 PM yesterday is 1 day ago, not 0.
 * WHY: card counts and drafts must agree; dividing instant differences by 24h and flooring
 * reported 19 days for a status entered on the calendar day 20 days back.
 */
export function calendarDaysSince(instant: Date, timezone: string, now: Date = new Date()): number {
  const tz = normalizeOrgTimezone(timezone);
  const local = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(instant);
  return -calendarDaysUntil(new Date(`${local}T00:00:00Z`), tz, now);
}

/**
 * WHY: Returns today's date as "YYYY-MM-DD" in local timezone.
 * Used for date-only string comparisons (expiration, overdue checks)
 * to avoid UTC midnight shifting dates back one day in US timezones.
 */
export function toLocalDateString(date?: Date): string {
  const d = date ?? new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * WHY: Safely parses a date value for comparison. When the input is a
 * date-only ISO string ("2026-02-17T00:00:00.000Z"), new Date() interprets
 * midnight UTC in local timezone, shifting it back one day in US timezones.
 * This helper strips the time portion for strings to preserve the calendar date.
 */
function parseDateSafe(date: Date | string): Date {
  if (date instanceof Date) return date;
  // Strip time portion from ISO strings to avoid timezone shift
  return parseISO(date.slice(0, 10));
}

export function isOverdue(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = parseDateSafe(date);
  // Check if date is before today (start of day)
  return isBefore(d, startOfDay(new Date()));
}

export function isDueSoon(date: Date | string | null | undefined, days = 7): boolean {
  if (!date) return false;
  const d = parseDateSafe(date);
  const today = startOfDay(new Date());
  const future = addDays(today, days);

  // It is due soon if it's after (or same as) today AND before (or same as) future date
  return !isBefore(d, today) && isBefore(d, future);
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) return "??";
  // WHY: noUncheckedIndexedAccess means array access returns T | undefined
  const firstPart = parts[0];
  const lastPart = parts[parts.length - 1];
  if (!firstPart) return "??";
  if (parts.length === 1) return firstPart.substring(0, 2).toUpperCase();
  if (!lastPart) return firstPart.substring(0, 2).toUpperCase();
  const firstChar = firstPart[0] ?? "";
  const lastChar = lastPart[0] ?? "";
  return (firstChar + lastChar).toUpperCase();
}

/**
 * Formats a string as a phone number: XXX-XXX-XXXX
 * Standardizes phone input for database consistency and UI uniformity.
 */
export function formatPhoneNumber(value: string): string {
  if (!value) return value;
  const digits = value.replace(/[^\d]/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Formats a string as an SSN: XXX-XX-XXXX
 * Standardizes SSN input for consistent storage and display.
 */
export function formatSSN(value: string): string {
  if (!value) return value;
  const digits = value.replace(/[^\d]/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
}

/**
 * Reduce raw TOTP field input to at most 6 digits.
 *
 * WHY: authenticator apps render codes with a readability space ("506 061"),
 * and users paste them. Relying on the input's maxLength to bound the field
 * truncates such a paste to "506 06" BEFORE any handler sees it, losing a real
 * digit. Callers pair this with a generous maxLength so the separator survives
 * long enough to be stripped here.
 */
export function extractTotpDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, TOTP_CODE_DIGITS);
}

/** Digit count of a TOTP code — must match the `digits` used in totp.ts. */
export const TOTP_CODE_DIGITS = 6;

/**
 * maxLength for a TOTP input. WHY LARGER THAN 6: leaves room for separators in
 * a pasted code; extractTotpDigits enforces the real 6-digit bound.
 */
export const TOTP_INPUT_MAX_LENGTH = 20;

/**
 * Formats file size in bytes to human-readable format
 * WHY: Display file sizes in KB, MB, GB instead of raw bytes
 */
/**
 * Escape ILIKE pattern metacharacters so user input is treated as literal text.
 * WHY order matters: backslashes escaped first to avoid double-escaping
 * the backslashes we insert for % and _.
 */
export function escapeIlikePattern(input: string): string {
  return input.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

/**
 * Format a SCREAMING_SNAKE enum value as a Title Case label ("NOT_STARTED" → "Not Started").
 * WHY: last-resort display for enum values without a config-registry label — prefer the
 * enum's dedicated config (e.g. location-participation-status-types) when one exists.
 */
export function formatEnumLabel(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format integer cents as a USD currency string ("5200" -> "$52.00").
 * WHY CENTS IN, DOLLARS OUT: Money is stored and passed around as integer cents to
 * avoid floating-point drift; the dollar conversion happens only here at the display edge.
 * Returns "-" for null/undefined so callers can render optional amounts uniformly.
 */
export function formatCentsAsCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

/**
 * Normalize an extracted credential/ID value for matching: trim → uppercase →
 * strip spaces and dashes. `"  ab-12 34 "` and `"AB1234"` both become `"AB1234"`.
 *
 * WHY null on non-string / empty: a missing or non-string extracted value cannot be
 * confidently matched. Every caller treats null as "no confident identity" and refuses
 * to collapse, match, or overwrite on a guess — that refusal is the safety rule, so the
 * null must survive rather than degrade into an empty-string match-everything key.
 *
 * WHY IN @/lib/utils: three subsystems now need the same normalization — read-time
 * Expirables de-dup (@/server/dto/expirable-dedup), the renewal-expiration sync, and the
 * document→ID upsert matcher. It is pure string manipulation with no server-only
 * dependency, so it lives here and each subsystem imports the one implementation instead
 * of forking a second one that could drift.
 */
export function normValue(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const cleaned = v.trim().toUpperCase().replace(/[\s-]/g, "");
  return cleaned.length > 0 ? cleaned : null;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
